/**
 * detectPlanQuery
 * ───────────────
 * Detects whether a user message is asking about a specific plan
 * by natural language (e.g. "how is the progress on Increase total revenue").
 *
 * If a plan is mentioned:
 *   - Searches only top-level plans in the current performance cycle
 *   - Returns the matched plan(s) so the caller can either:
 *       a) auto-select if 1 clear match
 *       b) ask for clarification if 2–3 close matches
 *       c) return "not found" message if 0 matches
 *
 * Also detects numbered list references like:
 *   - "tell me about the 3rd plan"
 *   - "1st, 4th, and 6th"
 *   - "2nd to 4th"
 *   - "the 3rd one"
 *
 * Returns null if the message is not asking about a specific plan.
 */

import SearchTopLevelPlansApi from '../api/plans/SearchTopLevelPlansApi';

// Intent patterns — more patterns, more coverage
const PLAN_INTENT_PATTERNS = [
  // "X tell me about this/it" or "X tell me" — plan name FIRST, request AFTER
  /^(.{4,200}?)\s+(?:tell\s+me(?:\s+about\s+(?:this|it|that))?|show\s+me|give\s+me(?:\s+(?:details|info|analysis))?|what\s+(?:about|is)\s+(?:this|it|that))(?:\s*[.?!]?\s*)?$/i,
  // "give me an analysis on the plan/goal/objective X" / "analysis on X"
  /(?:analysis\s+on(?:\s+the\s+(?:plan|goal|objective|strategy|tactic|initiative))?|analyse|analyze)\s+(?:the\s+(?:plan|goal|objective|strategy|tactic|initiative)\s+)?(.{6,200}?)(?:\s*\?|$)/i,
  // "progress on / status of / how is / tell me about X"
  /(?:progress(?:\s+on)?|status(?:\s+of)?|how\s+is|how'?s|tell\s+me\s+about|what\s+about|update\s+on|overview\s+of|details?\s+(?:on|for|of)|review)\s+(?:the\s+)?(?:(?:strategic\s+)?(?:objective|goal|plan|tactic|strategy|initiative|target)\s+)?(.{4,200}?)(?:\s*\?|$)/i,
  // "the plan/goal/objective X" or "plan X give me..."
  /\bthe\s+(?:strategic\s+)?(?:plan|goal|objective|strategy|tactic|initiative)\s+(.{4,200}?)(?:\s+(?:give|show|tell|provide|please|analysis|details?|progress|status|update|overview|review).*)?$/i,
  // "what is the progress/status/update of X"
  /(?:what\s+is\s+the\s+(?:progress|status|update|performance)\s+(?:of|on|for))\s+(.{6,200}?)(?:\s*\?|$)/i,
  // "goal X tell me" / "objective X show me" — plan level word first, name, then request
  /^(?:the\s+)?(?:strategic\s+)?(?:goal|objective|plan|tactic|strategy|initiative)\s+(.{4,200}?)\s+(?:tell\s+me|show\s+me|give\s+me|plan\s+tell\s+me|please)(?:.*)?$/i,
  // "goal X" / "objective X" directly (short message, level word + name)
  /^(?:the\s+)?(?:strategic\s+)?(?:goal|objective|tactic|strategy|initiative)\s+(.{4,200}?)(?:\s*\?|$)/i,
  // "X give me analysis" — plan name first, request second (allow longer names)
  /^(.{10,200}?)\s+(?:give\s+me|show\s+me|provide|run|do)\s+(?:an?\s+)?(?:analysis|update|progress|overview|status|review)/i,
  // "X analysis on this" / "X analysis" — plan name then request word
  /^(.{10,200}?)\s+(?:analysis|overview|update|status|progress|review)\s+(?:on\s+this|on\s+the\s+plan|please|now)?(?:\s*\?|$)/i,
  // "X plan tell me" / "X plan progress" — plan name ending with a level word + request
  /(.{4,200}?)\s+(?:plan|goal|objective)\s+(?:tell\s+me|progress|status|update|performance|analysis)(?:\s*\?|$)/i,
  // "X plan progress/status/performance"
  /(.{6,200}?)\s+(?:plan\s+)?(?:progress|status|update|performance|analysis)(?:\s*\?|$)/i,
];

// Words that indicate a general query (not a specific plan name) — skip these
const GENERAL_TERMS = [
  'performance cycle', 'the cycle', 'my plans', 'all plans', 'team', 'overall',
  'organisation', 'organization', 'unit', 'department', 'everyone', 'everything',
  'this', 'this plan', 'that', 'that plan', 'it', 'the plan', 'a plan',
];

// Plan level / hierarchy terms that users use to DESCRIBE a plan but aren't part of the title.
// e.g. "tell me about the goal 30 Qualified Leads" — "goal" is a level label, not part of the name.
// These get stripped from the search query before hitting the backend.
const PLAN_LEVEL_PREFIXES = [
  'strategic objective', 'strategic goal', 'strategic initiative',
  'objective', 'goal', 'tactic', 'strategy', 'initiative',
  'action', 'activity', 'measure', 'kra', 'key result',
  'plan', 'target',
];

// ─── Numbered reference parsing ───────────────────────────────────────────────

const ORDINAL_MAP = {
  '1st': 1, 'first': 1,
  '2nd': 2, 'second': 2,
  '3rd': 3, 'third': 3,
  '4th': 4, 'fourth': 4,
  '5th': 5, 'fifth': 5,
  '6th': 6, 'sixth': 6,
  '7th': 7, 'seventh': 7,
  '8th': 8, 'eighth': 8,
  '9th': 9, 'ninth': 9,
  '10th': 10, 'tenth': 10,
};

/**
 * Parse ordinal/number references from user text.
 * Supports:
 *  - "the 3rd plan" / "3rd one" / "number 3"
 *  - "1st, 4th, and 6th" / "1, 4 and 6"
 *  - "2nd to 4th" / "2 to 4" / "2-4"
 *  - "tell me about 3" / "show me 1 and 3"
 *
 * @returns {number[] | null} - 1-based indices or null if not a numbered reference
 */
export function parseNumberedReferences(text) {
  if (!text?.trim()) return null;
  const lower = text.toLowerCase().trim();

  // ── Range pattern: "2nd to 4th" / "2 to 4" / "2-4" / "from 2 to 4"
  const rangePattern = /(?:from\s+)?(\d+|1st|2nd|3rd|[4-9]th|10th|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth)\s*(?:to|-|through|thru)\s*(\d+|1st|2nd|3rd|[4-9]th|10th|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth)/i;
  const rangeMatch = lower.match(rangePattern);
  if (rangeMatch) {
    const start = parseOrdinalOrNumber(rangeMatch[1]);
    const end = parseOrdinalOrNumber(rangeMatch[2]);
    if (start && end && start <= end && start >= 1 && end <= 20) {
      const indices = [];
      for (let i = start; i <= end; i++) indices.push(i);
      return indices;
    }
  }

  // ── Multi-select pattern: "1st, 4th and 6th" / "1, 4, and 6" / "1 4 6"
  // Extract all numbers/ordinals from the text
  const allNumbers = [];
  const tokenPattern = /(\d+)(?:st|nd|rd|th)?|(?:first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth)/gi;
  let tokenMatch;
  while ((tokenMatch = tokenPattern.exec(lower)) !== null) {
    const val = parseOrdinalOrNumber(tokenMatch[0]);
    if (val && val >= 1 && val <= 20) allNumbers.push(val);
  }

  if (allNumbers.length > 0) {
    // Check if the message actually is about selecting from a list
    // (not just any message with a random number in it)
    const isSelectionIntent = /(?:tell\s+me|show\s+me|give\s+me|what\s+about|details?\s+(?:on|for|of|about)|info\s+(?:on|about)|about|the|plan|one|option|number|#|select)/i.test(lower)
      || /^\s*(?:\d+[\s,]*)+\s*$/.test(lower.replace(/(?:and|&|,)/g, ' ').trim()) // just numbers: "1 4 6"
      || allNumbers.length >= 2; // multiple numbers strongly implies list selection
    
    if (isSelectionIntent) {
      return [...new Set(allNumbers)]; // deduplicate
    }
  }

  return null;
}

function parseOrdinalOrNumber(token) {
  if (!token) return null;
  const lower = token.toLowerCase().trim();
  if (ORDINAL_MAP[lower]) return ORDINAL_MAP[lower];
  // strip ordinal suffix: "3rd" -> "3"
  const num = parseInt(lower.replace(/(?:st|nd|rd|th)$/i, ''), 10);
  return isNaN(num) ? null : num;
}

/**
 * Resolve numbered references against a previously shown plan list.
 *
 * @param {string} message - user message
 * @param {object[]} lastPlanList - array of plan objects that were last shown
 * @returns {{ type: 'numbered_single' | 'numbered_multi' | null, plans?: object[], indices?: number[] }}
 */
export function resolveNumberedPlanReferences(message, lastPlanList) {
  if (!lastPlanList || lastPlanList.length === 0) return { type: null };

  const indices = parseNumberedReferences(message);
  if (!indices || indices.length === 0) return { type: null };

  // Map 1-based indices to 0-based, filter out of range
  const resolvedPlans = [];
  const validIndices = [];
  for (const idx of indices) {
    if (idx >= 1 && idx <= lastPlanList.length) {
      resolvedPlans.push(lastPlanList[idx - 1]);
      validIndices.push(idx);
    }
  }

  if (resolvedPlans.length === 0) {
    return { type: null };
  }

  if (resolvedPlans.length === 1) {
    return { type: 'numbered_single', plans: resolvedPlans, indices: validIndices };
  }

  return { type: 'numbered_multi', plans: resolvedPlans, indices: validIndices };
}

/**
 * @param {string} message
 * @param {string} performanceCycleId   - from perfCycleState.id
 * @param {string} cycleName            - for user-facing messages
 * @returns {Promise<{
 *   type: 'single' | 'ambiguous' | 'not_found' | 'no_match',
 *   plan?: object,           // if type === 'single'
 *   plans?: object[],        // if type === 'ambiguous'
 *   query?: string,
 *   clarificationMessage?: string,
 *   notFoundMessage?: string,
 * }>}
 */
export async function detectPlanQuery(message, performanceCycleId, cycleName) {
  if (!message?.trim() || !performanceCycleId) return { type: 'no_match' };

  // ── Explicit reference guard ──────────────────────────────────────────────
  // If the message already carries an explicit "#<id>:<title>" reference (e.g.
  // the user picked a plan from the # autocomplete, or a prior turn injected it),
  // the plan is ALREADY identified by id. Skip all NLP/title-search — otherwise
  // we'd fuzzy-search plan titles for a string like "#41754390:Achieve 10MN",
  // which never matches and wrongly returns "not found".
  // Returning 'no_match' lets the caller pass the message straight to the
  // backend, where chatbot-context.service resolves "#id:title" by id.
  if (/#\d+\s*:/.test(message)) {
    return { type: 'no_match' };
  }

  const lower = message.toLowerCase();

  // Quick reject: general terms don't refer to a specific plan
  if (GENERAL_TERMS.some(t => lower.includes(t) && lower.length < 60)) {
    return { type: 'no_match' };
  }

  // Try to extract the plan name portion from the message
  let extractedQuery = null;
  for (const pattern of PLAN_INTENT_PATTERNS) {
    const match = message.match(pattern);
    if (match) {
      // Take the captured group, strip trailing filler
      let candidate = match[1]
        ?.replace(/\?+$/, '')
        ?.replace(/\s+(please|now|today|this week|for me|here|right now|asap)$/i, '')
        ?.replace(/^(?:the\s+plan\s+|plan\s+)/i, '')
        ?.trim();

      // Must be at least 6 chars and not a generic term
      if (candidate && candidate.length >= 6 && !GENERAL_TERMS.some(t => candidate.toLowerCase() === t)) {
        // If candidate is very long (e.g. repeated sentence), take the first sentence only
        const firstSentence = candidate.split(/[.!?]/)[0]?.trim();
        extractedQuery = (firstSentence && firstSentence.length >= 6) ? firstSentence : candidate.substring(0, 120).trim();
        break;
      }
    }
  }

  if (!extractedQuery) return { type: 'no_match' };

  // Strip plan level prefixes from the query.
  // e.g. "goal 30 Qualified Leads" → "30 Qualified Leads"
  // e.g. "strategic objective Increase Revenue" → "Increase Revenue"
  let cleanedQuery = extractedQuery;
  for (const prefix of PLAN_LEVEL_PREFIXES) {
    const prefixPattern = new RegExp(`^(?:the\\s+)?${prefix}\\s+`, 'i');
    if (prefixPattern.test(cleanedQuery)) {
      cleanedQuery = cleanedQuery.replace(prefixPattern, '').trim();
      break; // only strip one prefix
    }
  }
  // Use the cleaned version if it's still meaningful (>= 4 chars)
  const searchQuery = cleanedQuery.length >= 4 ? cleanedQuery : extractedQuery;

  // Search the backend — searches top-level first, then cascades/splits
  const result = await SearchTopLevelPlansApi(searchQuery, performanceCycleId);

  if (!result?.success) return { type: 'no_match' };

  const plans = result.data || [];

  if (plans.length === 0) {
    // Fuzzy match failed — try a broader search by splitting query into key words
    // e.g. "Grow Rockland local product" -> search for significant words
    const words = searchQuery.split(/\s+/).filter(w => w.length > 3);
    if (words.length >= 2) {
      // Try searching with fewer words (most distinctive ones)
      const shorterQuery = words.slice(0, 3).join(' ');
      const retryResult = await SearchTopLevelPlansApi(shorterQuery, performanceCycleId);
      if (retryResult?.success && retryResult.data?.length > 0) {
        const retryPlans = retryResult.data;
        if (retryPlans.length === 1) {
          return { type: 'single', plan: retryPlans[0], query: searchQuery };
        }
        // Multiple matches — ask user to pick
        const listText = retryPlans
          .map((p, i) => {
            const levelTag = p.planLevel ? ` _(${p.planLevel})_` : '';
            const typeTag = p.planType && p.planType !== 'top-level' ? ` [${p.planType} of: ${p.parentPlanTitle || 'parent'}]` : '';
            return `${i + 1}. **${p.title}**${levelTag}${typeTag}`;
          })
          .join('\n');

        return {
          type: 'ambiguous',
          plans: retryPlans,
          query: searchQuery,
          clarificationMessage: `I couldn't find an exact match for "${searchQuery}", but I found these similar plans:\n\n${listText}\n\nWhich one did you mean? You can reply with the number (e.g. "1" or "the 2nd one"), multiple numbers (e.g. "1, 3, and 5"), or a range (e.g. "2 to 4").`,
        };
      }
    }

    return {
      type: 'not_found',
      query: searchQuery,
      notFoundMessage: `I couldn't find a plan matching "${searchQuery}" in your current cycle (${cycleName || 'active cycle'}). I searched across all levels — strategic objectives, goals, cascades, and split plans. Could you double-check the name? You can also use **#** to browse and select plans from the autocomplete list.`,
    };
  }

  if (plans.length === 1) {
    return { type: 'single', plan: plans[0], query: searchQuery };
  }

  // 2–5 matches — ask for clarification with level info
  const listText = plans
    .map((p, i) => {
      const levelTag = p.planLevel ? ` _(${p.planLevel})_` : '';
      const typeTag = p.planType && p.planType !== 'top-level' ? ` [${p.planType} of: ${p.parentPlanTitle || 'parent'}]` : '';
      return `${i + 1}. **${p.title}**${levelTag}${typeTag}`;
    })
    .join('\n');

  return {
    type: 'ambiguous',
    plans,
    query: searchQuery,
    clarificationMessage: `I found ${plans.length} plans matching "${searchQuery}" in ${cycleName || 'the current cycle'}:\n\n${listText}\n\nWhich one would you like to know about? You can reply with the number (e.g. "1" or "the 3rd one"), select multiple (e.g. "1, 3, and 5"), or a range (e.g. "2 to 4").`,
  };
}
